import { useEffect, useState } from 'react';
import {
  KENO_REVEAL_MS,
  kenoRevealAnimates,
  kenoRevealAt,
  nextBallAt,
  revealCount,
} from '../components/kenoBoardModel.js';

/**
 * How many balls are out right now. The TV and the phone both run this off
 * the same server deadline, so neither screen calls a number before the
 * other: the reveal ends `KENO_REVEAL_MS` after it starts, just ahead of the
 * settle. A settled draw, a missing deadline or reduced motion shows the
 * whole draw at once.
 *
 * `reduced` comes from the shell: the web reads the media query, native reads
 * the OS accessibility setting. The hook itself touches neither.
 */
export function useKenoReveal(
  draw: readonly number[] | null,
  settled: boolean,
  endsAt: number | null,
  reduced: boolean,
): number {
  const drawKey = draw?.join('-') ?? '';
  const animating = kenoRevealAnimates(draw, settled, endsAt, reduced);
  const [now, setNow] = useState(() => (animating ? Date.now() : 0));

  useEffect(() => {
    if (!animating || draw === null || endsAt === null) return;
    const startedAt = endsAt - KENO_REVEAL_MS;
    const size = draw.length;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Wake once per ball rather than polling: a weak cast device re-renders
    // the whole board on every tick, and between balls nothing changes.
    const tick = () => {
      const current = Date.now();
      setNow(current);
      const count = revealCount(startedAt, endsAt, current, size);
      if (count >= size) return;
      timer = setTimeout(tick, Math.max(1, nextBallAt(startedAt, count, size) - current));
    };
    tick();
    return () => clearTimeout(timer);
  }, [animating, drawKey, endsAt]);

  return kenoRevealAt(draw, settled, endsAt, reduced, now);
}
